// ** Modules
import React from 'react';

// ** UI
import Box from '@mui/material/Box';
import Badge from '@mui/material/Badge';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import ButtonBase from '@mui/material/ButtonBase';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import { useTheme } from '@mui/material/styles';

// ** Components
import Section from '@/components/common/Section';
import EhrenamtCardsGrid from './EhrenamtCardsGrid';
import ComponentBlocksSearchFilter from '@/components/strapi/Shared/ComponentBlocksSearchFilter';
import FileCard, { downloadByUrl } from '@/components/common/FileCard';
import { Icon } from '@/components/common/icons';

// ** Utils
import { getStrapiFileUrl } from '@/utils/api/strapi';
import { useGetEhrenamtDocumentsConnection } from '@/lib/utils/ehrenamtDataUtils';

// ** Types
import type { SearchOption } from '@/components/common/searchSection';
import type { User } from 'next-auth';
import type { SxProps, Theme } from '@mui/material/styles';

type ComponentBlocksEhrenamtBlockProps = {
	currentUser?: User | null;
	containerSx?: SxProps<Theme>;
	ehrenamtSearchFilter?: Record<string, any>;
};

const deselectAllLabel = 'Alle abwählen';

const ComponentBlocksEhrenamtBlock = ({
	currentUser,
	containerSx,
	ehrenamtSearchFilter
}: ComponentBlocksEhrenamtBlockProps) => {
	const theme = useTheme();
	const { nodes: documents, loading: isLoading, error } = useGetEhrenamtDocumentsConnection(currentUser?.groups || []);

	// Filter States
	const [selectedCategory, setSelectedCategory] = React.useState<string>('all');
	const [selectedPeriod, setSelectedPeriod] = React.useState<string>('');
	const [searchTerm, setSearchTerm] = React.useState<string>('');
	const [selectedIds, setSelectedIds] = React.useState<Set<string>>(new Set());

	const dynamicCategoryOptions = React.useMemo(() => {
		if (!documents.length) return [];

		const optionsMap = new Map<string, SearchOption>();
		optionsMap.set('all', { value: 'all', label: 'Alle meine Gremien' });

		documents.forEach((doc) => {
			const rawCategory = doc.ehrenamtCategory;
			if (!rawCategory || typeof rawCategory !== 'string') return;

			const cleanCategory = rawCategory.trim();
			if (!cleanCategory) return;

			let cleanLabel = cleanCategory.replace(/^(Ausschuss_|Funktion_|\d+\s*)/g, '');
			cleanLabel = cleanLabel.replace(/[_/]+/g, ' ').trim();
			cleanLabel = cleanLabel.charAt(0).toUpperCase() + cleanLabel.slice(1);

			if (!optionsMap.has(cleanCategory)) {
				optionsMap.set(cleanCategory, {
					value: cleanCategory,
					label: cleanLabel
				});
			}
		});

		const generatedOptions = Array.from(optionsMap.values());
		return generatedOptions;
	}, [documents]);

	const dynamicPeriodOptions = React.useMemo(() => {
		const availableYears = new Set<string>();

		documents.forEach((doc) => {
			const dateString = typeof doc.date === 'string' ? doc.date : String(doc.date);

			const matchesCategory = selectedCategory === 'all' || doc.ehrenamtCategory === selectedCategory;

			if (matchesCategory && doc?.date) {
				const year = dateString.split('-')[0];
				availableYears.add(year);
			}
		});

		const options = Array.from(availableYears)
			.sort((a, b) => b.localeCompare(a))
			.map((y) => ({ value: y, label: y }));

		return [{ value: '', label: 'Alle Jahre' }, ...options];
	}, [documents, selectedCategory]);

	const visibleFiles = React.useMemo(() => {
		const term = searchTerm.trim().toLowerCase();

		const filtered = documents.filter((doc) => {
			const dateString = typeof doc.date === 'string' ? doc.date : String(doc.date);
			const docYear = dateString.split('-')[0];
			const mainFile = doc?.files?.[0];

			const matchesCategory = selectedCategory === 'all' || doc.ehrenamtCategory === selectedCategory;
			const matchesYear = !selectedPeriod || docYear === selectedPeriod;
			const matchesSearch =
				!term ||
				(mainFile?.name || '').toLowerCase().includes(term) ||
				(doc?.ehrenamtDocumentDescription || '').toLowerCase().includes(term);

			return matchesCategory && matchesYear && matchesSearch;
		});

		return filtered;
	}, [searchTerm, selectedCategory, selectedPeriod, documents]);

	const selectedCount = visibleFiles.filter((f) => selectedIds.has(f.documentId)).length;

	const isAllSelected = visibleFiles.length > 0 && selectedCount === visibleFiles.length;

	const handleSelectAll = React.useCallback(() => {
		const visibleIds = visibleFiles.map((f) => f.documentId);
		setSelectedIds((prev) => {
			const next = new Set(prev);
			if (isAllSelected) {
				visibleIds.forEach((id) => {
					next.delete(id);
				});
			} else {
				visibleIds.forEach((id) => {
					next.add(id);
				});
			}
			return next;
		});
	}, [visibleFiles, isAllSelected]);

	const handleDownloadSelected = React.useCallback(() => {
		const host = typeof window !== 'undefined' ? window.location.host : '';
		visibleFiles.forEach((doc) => {
			if (selectedIds.has(doc.documentId)) {
				const file = doc.files?.[0];
				if (file?.url) {
					const fullUrl = getStrapiFileUrl({ host, url: file.url });
					downloadByUrl(fullUrl, file.name);
				}
			}
		});
	}, [visibleFiles, selectedIds]);

	if (isLoading)
		return (
			<Box sx={{ textAlign: 'center', py: 10 }}>
				<CircularProgress />
			</Box>
		);
	if (error)
		return (
			<Typography color="error" sx={{ textAlign: 'center' }}>
				Fehler beim Laden.
			</Typography>
		);

	return (
		<Section containerSx={containerSx}>
			<ComponentBlocksSearchFilter
				config={ehrenamtSearchFilter}
				options={{
					categories: dynamicCategoryOptions,
					periods: dynamicPeriodOptions
				}}
				state={{
					selectedCategory: selectedCategory,
					selectedPeriod: selectedPeriod,
					searchTerm: searchTerm
				}}
				handlers={{
					onCategoryChange: (val) => setSelectedCategory(val ?? 'all'),
					onPeriodChange: (val) => setSelectedPeriod(val ?? ''),
					onSearchChange: setSearchTerm,
					onSearchSubmit: setSearchTerm
				}}
				backgroundColor="#FAFAFA"
			/>

			{visibleFiles.length > 0 && (
				<Box
					sx={{
						mt: { xs: 16, md: 20 },
						mb: { xs: 8, md: 7 },
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
						gap: 8
					}}
				>
					<Typography variant="body-md">{`${visibleFiles.length} Downloads verfügbar`}</Typography>
					<Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
						<ButtonBase onClick={handleSelectAll}>
							<Typography variant="body-md">{isAllSelected ? deselectAllLabel : 'Alle auswählen'}</Typography>
						</ButtonBase>

						<Badge
							badgeContent={selectedCount}
							invisible={selectedCount === 0}
							sx={{
								'& .MuiBadge-badge': {
									backgroundColor: theme.palette.primary.main,
									color: 'white'
								}
							}}
						>
							<IconButton onClick={handleDownloadSelected} disabled={selectedCount === 0} size="small">
								<Icon name="download" />
							</IconButton>
						</Badge>
					</Stack>
				</Box>
			)}

			<EhrenamtCardsGrid showAvailabilityCount={false}>
				{visibleFiles.map((doc) => {
					const hostUrl = typeof window !== 'undefined' ? window.location.host : '';

					return (
						<FileCard
							key={doc.documentId}
							item={{
								documentId: doc.documentId,
								fileName: doc.files?.[0]?.name || 'Dokument',
								url: doc.files?.[0]?.url
									? getStrapiFileUrl({
											host: hostUrl,
											url: doc.files[0].url
										})
									: '',
								date: typeof doc?.date === 'string' ? doc.date : '',
								description: doc?.ehrenamtDocumentDescription || '',
								updatedAt: doc.files?.[0]?.updatedAt ? String(doc.files[0].updatedAt) : undefined,
								ext: doc.files?.[0]?.ext?.replace('.', '') || 'pdf'
							}}
							showPreview
							selected={selectedIds.has(doc.documentId)}
							onSelectedChange={(val) => {
								setSelectedIds((prev) => {
									const next = new Set(prev);
									val ? next.add(doc.documentId) : next.delete(doc.documentId);
									return next;
								});
							}}
						/>
					);
				})}
			</EhrenamtCardsGrid>
		</Section>
	);
};

export default ComponentBlocksEhrenamtBlock;
